import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { FaCubes, FaDatabase, FaLink, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminFormLayout from './AdminFormLayout';
import Navigation from './Navigation';
import {
    filterMenuModuleRecords,
    findOutletMenuModuleByPath,
    isRegisteredOutletMenuPath,
    outletMenuModuleCatalog,
} from './outletMenuModuleRegistry';

const ACTIVE_STATUS_VALUE = '1';
const INACTIVE_STATUS_VALUE = '0';

function AddModule() {
    const navigate = useNavigate();
    const [isRouteLoading, setIsRouteLoading] = useState(true);
    const [usedModuleLinks, setUsedModuleLinks] = useState(new Set());
    const [moduleData, setModuleData] = useState({
        Modulename: '',
        Modulelink: '',
        Modulestatus: '',
        Filename: '',
        Tablename: '',
        Databasename: '',
    });

    const availableModuleOptions = useMemo(
        () => outletMenuModuleCatalog.filter(module => !usedModuleLinks.has(module.path)),
        [usedModuleLinks]
    );

    const fetchExistingModules = useCallback(async () => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            setIsRouteLoading(false);
            return;
        }

        setIsRouteLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}module-master-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ limit: 200, start: 0 }),
            });

            const data = await response.json();
            if (data.error === "0") {
                const existingModules = filterMenuModuleRecords(data.results);
                setUsedModuleLinks(
                    new Set(
                        existingModules
                            .map(module => module.Modulelink || module.module_link || module.path || '')
                            .filter(Boolean)
                    )
                );
                return;
            }

            throw new Error(data.message || 'Failed to fetch existing modules');
        } catch (err) {
            console.error('Failed to fetch existing modules:', err);
            Swal.fire('Error!', 'Failed to load existing module routes.', 'error');
        } finally {
            setIsRouteLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExistingModules();
    }, [fetchExistingModules]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setModuleData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleMenuModuleChange = (value) => {
        const selectedModule = findOutletMenuModuleByPath(value);
        setModuleData(prevData => ({
            ...prevData,
            Modulelink: selectedModule?.path || value,
            Modulename: isRegisteredOutletMenuPath(value)
                ? selectedModule?.label || prevData.Modulename
                : prevData.Modulename,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}addmodule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(moduleData),
            });

            const data = await response.json();
            if (data.error === "0") {
                Swal.fire('Success!', 'Module added successfully.', 'success');
                navigate('/modules');
            } else {
                Swal.fire('Error!', data.message || 'Failed to add module.', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error!', 'Failed to add module.', 'error');
        }
    };

    const handleCancel = () => {
        navigate('/modules');  // Navigate back to modules list
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                fluid={false}
                containerClassName="admin-page-shell mt-4"
                icon={FaCubes}
                eyebrow="Module setup"
                title="Add Module"
                description="Tambahkan modul baru agar fitur admin bisa dipetakan rapi ke route, file, dan tabel terkait."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Module List', to: '/modules' },
                    { label: 'Add Module' },
                ]}
                tips={[
                    'Gunakan nama modul yang singkat dan konsisten agar mudah dicari di daftar modul.',
                    'Pastikan route, file, dan table name sesuai dengan struktur backend yang aktif.',
                    'Isi status modul dengan nilai yang benar sebelum modul dipakai oleh admin.',
                ]}
            >
                <Form onSubmit={handleSubmit}>
                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaLink />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Module identity</h2>
                                <p className="form-section__description">
                                    Nama, route, dan status akan tampil sebagai identitas utama modul di admin panel.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group controlId="formModuleName" className="field-stack span-6">
                                <Form.Label>Module Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Modulename"
                                    value={moduleData.Modulename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter module name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formModuleLink" className="field-stack span-6">
                                <Form.Label>Module Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    list="module-route-options"
                                    value={moduleData.Modulelink}
                                    onChange={event => handleMenuModuleChange(event.target.value)}
                                    disabled={isRouteLoading}
                                    placeholder="/route-module-baru"
                                    required
                                />
                                <datalist id="module-route-options">
                                    {availableModuleOptions.map(module => (
                                        <option key={module.path} value={module.path}>
                                            {module.label}
                                        </option>
                                    ))}
                                </datalist>
                                <Form.Text>
                                    Pilih route dari suggestion atau ketik path manual. Route yang sudah dipakai tetap disembunyikan dari suggestion agar tidak duplikat.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group controlId="formModuleStatus" className="field-stack span-12">
                                <Form.Label>Module Status</Form.Label>
                                <Form.Select
                                    name="Modulestatus"
                                    value={moduleData.Modulestatus}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select status</option>
                                    <option value={ACTIVE_STATUS_VALUE}>Active</option>
                                    <option value={INACTIVE_STATUS_VALUE}>Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </section>

                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaDatabase />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Technical mapping</h2>
                                <p className="form-section__description">
                                    Hubungkan modul ke file dan tabel yang dipakai oleh source code.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group controlId="formFileName" className="field-stack span-4">
                                <Form.Label>File Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Filename"
                                    value={moduleData.Filename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter file name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formTableName" className="field-stack span-4">
                                <Form.Label>Table Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Tablename"
                                    value={moduleData.Tablename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter table name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formDatabaseName" className="field-stack span-4">
                                <Form.Label>Database Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Databasename"
                                    value={moduleData.Databasename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter database name"
                                />
                            </Form.Group>
                        </div>
                    </section>

                    <div className="form-actions">
                        <Button variant="secondary" onClick={handleCancel}>
                            <FaTimes /> Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            <FaSave /> Save Module
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default AddModule;
